import React, { useState, useEffect } from 'react';
import { cardsApi, banlistsApi } from '../services/api';
import { Card, BannedCard, BannedCardCreate } from '../types';

interface CardSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  rulesetId: string;
  onBannedCardsChange: (cards: BannedCard[]) => void;
  existingBannedCards: BannedCard[];
}

const CardSearchOverlay: React.FC<CardSearchOverlayProps> = ({
  isOpen,
  onClose,
  rulesetId,
  onBannedCardsChange,
  existingBannedCards,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCards, setSelectedCards] = useState<BannedCard[]>(existingBannedCards);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedCards(existingBannedCards);
    }
  }, [isOpen, existingBannedCards]);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await cardsApi.search(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const isCardBanned = (oracleId: string) => {
    return selectedCards.some(card => card.oracle_id === oracleId);
  };

  const handleAddCard = async (card: Card) => {
    if (isCardBanned(card.oracle_id)) return;

    setSaving(true);
    try {
      const bannedCardData: BannedCardCreate = {
        oracle_id: card.oracle_id,
        card_name: card.name,
        reason: '',
      };

      const response = await banlistsApi.addCard(rulesetId, bannedCardData);
      const newBannedCard = response.data;
      
      setSelectedCards(prev => [...prev, newBannedCard]);
      onBannedCardsChange([...selectedCards, newBannedCard]);
    } catch (error) {
      console.error('Failed to add card to banlist:', error);
      alert('Failed to add card to banlist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCard = async (bannedCardId: string) => {
    setSaving(true);
    try {
      await banlistsApi.removeCard(rulesetId, bannedCardId);
      
      const updatedCards = selectedCards.filter(card => card.id !== bannedCardId);
      setSelectedCards(updatedCards);
      onBannedCardsChange(updatedCards);
    } catch (error) {
      console.error('Failed to remove card from banlist:', error);
      alert('Failed to remove card from banlist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Manage Banlist</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left side - Search */}
          <div className="w-1/2 border-r p-6 flex flex-col">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Cards
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter card name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => handleSearch(searchQuery)}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="text-center py-4">
                  <div className="text-gray-600">Searching...</div>
                </div>
              )}

              {!loading && searchResults.length === 0 && searchQuery && (
                <div className="text-center py-4">
                  <div className="text-gray-600">No cards found</div>
                </div>
              )}

              {!loading && searchResults.length > 0 && (
                <div className="space-y-2">
                  {searchResults.map((card) => (
                    <div
                      key={card.id}
                      className="flex items-center p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-shrink-0 mr-3">
                        {card.image_uri ? (
                          <img
                            src={card.image_uri}
                            alt={card.name}
                            className="w-12 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-xs text-gray-500">No Image</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {card.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {card.set_name} ({card.set_code})
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddCard(card)}
                        disabled={isCardBanned(card.oracle_id) || saving}
                        className={`px-3 py-1 rounded text-sm font-medium ${
                          isCardBanned(card.oracle_id)
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                      >
                        {isCardBanned(card.oracle_id) ? 'Banned' : 'Ban'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Banned cards */}
          <div className="w-1/2 p-6 flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Banned Cards ({selectedCards.length})
            </h3>

            <div className="flex-1 overflow-y-auto">
              {selectedCards.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-600">No banned cards yet</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedCards.map((bannedCard) => (
                    <div
                      key={bannedCard.id}
                      className="flex items-center p-3 border rounded-lg bg-red-50"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {bannedCard.card_name}
                        </div>
                        {bannedCard.reason && (
                          <div className="text-sm text-gray-500">
                            Reason: {bannedCard.reason}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCard(bannedCard.id)}
                        disabled={saving}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6">
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardSearchOverlay;
